import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActiveOrderDsService, QaHistoryDsService } from '@app/core/data-services';
import { NextSetupSubphaseCommandService } from '@app/modules/setup-phase/commands';
import { OrderPhaseService } from '@app/shared/components';
import { nav } from '@app/shared/utils';
import { Observable, Subject, Subscription } from 'rxjs';
import { SetupPhaseService } from '../../services/setup-phase/setup-phase.service';
import { ProductionQaCheck, RunSubPhaseType } from 'chronos-core-client';
import { LineClearanceService } from '../../services/line-clearance/line-clearance.service';
import { debounceTime, shareReplay, take } from 'rxjs/operators';
import { QaCheckService } from '@app/core/services';
import { SignatureHandlerService } from '@app/core/services/signature/signature-handler.service';
import { QaCheckType, SignatureConfig } from '@app/shared/models/signature';
import { SignatureComponent } from '@app/shared/components/signature/signature.component';
import { EmployeeSignatureDsService } from '@app/core/data-services/employee-signature-ds/employee-signature-ds.service';
import { ActiveWorkCenterService } from '@app/core/workcenter';

@Component({
  selector: 'app-line-clearance',
  templateUrl: './line-clearance.component.html',
  styleUrls: ['./line-clearance.component.scss']
})
export class LineClearanceComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scanInput', { static: true }) public scanInput: ElementRef;
  @ViewChild('clearance') public clearance: ElementRef;
  @ViewChild('leftSignature') public leftSignatureComponent: SignatureComponent;
  @ViewChild('rightSignature') public rightSignatureComponent: SignatureComponent;

  public fields$: Observable<ProductionQaCheck[]>;
  public nextCommandService: NextSetupSubphaseCommandService;
  public isCapsLockOn = false;
  public scanInputText;
  public isSignatureFeatureEnabled = false;
  public signatureConfig = {} as SignatureConfig;
  public leftSignatureQaChecks: ProductionQaCheck[] = [];
  public rightSignatureQaChecks: ProductionQaCheck[] = [];
  public isReadOnly = false;
  public lineClearanceListHasFocus = false;

  private isNextCommandEnabled = false;
  private input$ = new Subject<string>();
  private productionOrderId: number;
  private subscriptions = new Subscription();
  private areQAChecksSigned = false;
  private areQaChecksValid = false;
  private signatureState: { leftSigned: boolean; rightSigned: boolean } = { leftSigned: false, rightSigned: false };
  setupPhaseClickHandler: (event: MouseEvent) => void;
  setupPhaseElement: HTMLElement;
  scanInputFocusHandler: (event: FocusEvent) => void;

  constructor(
    private lineClearanceService: LineClearanceService,
    private activeOrderDsService: ActiveOrderDsService,
    private setupPhaseService: SetupPhaseService,
    private setupFooterService: OrderPhaseService,
    private changeDetector: ChangeDetectorRef,
    private qaCheckService: QaCheckService,
    private qaHistoryDsService: QaHistoryDsService,
    private signatureHandlerService: SignatureHandlerService,
    private employeeSignatureDsService: EmployeeSignatureDsService,
    private workCenterService: ActiveWorkCenterService
  ) {
    this.input$.pipe(debounceTime(30000)).subscribe(() => {
      this.scanInputText = '';
    });
  }

  public ngOnInit(): void {
    this.productionOrderId = this.activeOrderDsService.getActiveOrderId();
    this.fields$ = this.lineClearanceService.getQACheckFields(this.productionOrderId).pipe(shareReplay(1));
    this.setCommands();

    this.subscriptions.add(
      this.qaHistoryDsService.isQaHistoryAvailableForSetup().subscribe((isAvailable) => {
        this.updateNextCommand(isAvailable);
      })
    );

    this.lineClearanceService.setFocus$.subscribe(() => {
      this.setFocusScanInput();
    });

    this.lineClearanceService.checkCapsLock$.subscribe((event) => {
      this.manageCapsLock(event);
    });

    this.lineClearanceService.validation$.subscribe((invalidItems) => {
      this.nextCommandService.enabled = invalidItems.length === 0;
      this.isNextCommandEnabled = invalidItems.length === 0;
    });

    this.isSignatureFeatureEnabled = this.signatureHandlerService.isQualityCheckSignatureEnabled();
    if (this.isSignatureFeatureEnabled) {
      this.signatureConfig = {
        isSignatureFeatureEnabled: this.isSignatureFeatureEnabled,
        qaCheckType: QaCheckType.LineClearance
      };

      this.setupFilterProductionQaCheck();
    }

    this.isReadOnly = this.signatureHandlerService.setReadOnlyState(true);
    this.SetfocusScanField();

    this.subscriptions.add(
      this.signatureHandlerService.resetSignature$.subscribe(() => {
        this.areQAChecksSigned = false;
        this.removeSignature();
      })
    );
  }

  public ngAfterViewInit(): void {
    this.setFocusScanInput();
    this.lineClearanceListHasFocus = true;

    const eventHandler = (event: KeyboardEvent | MouseEvent) => {
      if (event?.type === 'keyup' && !(event instanceof KeyboardEvent) && !(event instanceof MouseEvent)) {
        return;
      }
      this.manageCapsLock(event);
      this.changeDetector.markForCheck();
      this.setFocusScanInput();
    };

    this.scanInput?.nativeElement?.addEventListener('keyup', eventHandler);
    this.scanInput?.nativeElement?.addEventListener('mousedown', eventHandler);

    // Add focus and blur listeners for scanInput
    this.scanInputFocusHandler = (event: FocusEvent) => {
      this.lineClearanceListHasFocus = event.type === 'focus';
    };

    this.scanInput?.nativeElement?.addEventListener('focus', this.scanInputFocusHandler);
    this.scanInput?.nativeElement?.addEventListener('blur', this.scanInputFocusHandler);

    const onClickHandler = (event: MouseEvent) => {
      this.SetfocusScanField();
      this.manageCapsLock(event);
      event.stopPropagation();
      console.info('Line Clearance: onClick');
    };

    // Find the parent app-setup-phase element and register click handler
    const setupPhaseElement = this.findParentElement('app-setup-phase');
    if (setupPhaseElement) {
      setupPhaseElement.addEventListener('click', onClickHandler);

      // Store reference for cleanup
      this.setupPhaseClickHandler = onClickHandler;
      this.setupPhaseElement = setupPhaseElement;
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    // Clean up the event listeners
    if (this.setupPhaseElement && this.setupPhaseClickHandler) {
      this.setupPhaseElement.removeEventListener('click', this.setupPhaseClickHandler);
    }

    if (this.scanInput?.nativeElement) {
      this.scanInput.nativeElement.removeEventListener('focus', this.scanInputFocusHandler);
      this.scanInput.nativeElement.removeEventListener('blur', this.scanInputFocusHandler);
    }
  }

  @HostListener('window:keydown.enter', ['$event'])
  public SetfocusScanField() {
    if (this.isFocusAvailable() && !this.isReadOnly) {
      this.setFocusScanInput();
    }

    this.manageCapsLock();
  }

  public onFormValidityChange(isQaCheckValid: boolean): void {
    if (this.isSignatureFeatureEnabled) {
      this.enableEnterNextCommand(isQaCheckValid, this.areQAChecksSigned);
    } else {
      this.enableEnterNextCommand(isQaCheckValid);
    }
  }

  private enableEnterNextCommand(isQaCheckValid: boolean, isLeftRightSignatureSigned?: boolean): void {
    const customValidation = this.lineClearanceService.validationSubject.value?.length;
    this.areQaChecksValid  = (isQaCheckValid && customValidation === 0);
    this.nextCommandService.enabled = (this.areQaChecksValid && (isLeftRightSignatureSigned ?? true));
    this.isNextCommandEnabled = (isQaCheckValid && customValidation === 0 && (isLeftRightSignatureSigned ?? true));
  }

  public onBlur(event: KeyboardEvent | MouseEvent) {
    if (event?.type === 'keyup' && !(event instanceof KeyboardEvent) && !(event instanceof MouseEvent)) {
      return;
    }

    this.SetfocusScanField();
    this.manageCapsLock(event);
  }

  public onInput(value: string) {
    this.input$.next(value);
  }

  public onEnter(value: string) {
    console.info('Scanned Value', value);
    this.qaCheckService.setCheckResultMultipleRows(this.fields$, value).subscribe((res) => {
      if (res) {
        this.fields$ = this.lineClearanceService.getQACheckFields(this.productionOrderId);
        this.signatureHandlerService.resetSignature();
      }
    });

    this.scanInputText = '';
  }

  public setActiveSignature(position: string): void {
    const signatureComponent = this.getSignatureComponent(position);
    if (signatureComponent && signatureComponent.scanSignatureInput) {
      signatureComponent.scanSignatureInput.nativeElement.focus();
    }

    this.changeDetector.detectChanges();
  }

  public onQaSignatureScanned(isScanned: boolean): void {
    if (!isScanned) {
      return;
    }

    // First scan marks the left signature, second scan marks the right signature
    if (!this.signatureState.leftSigned) {
      this.signatureState.leftSigned = true;
      return;
    }

    if (!this.signatureState.rightSigned) {
      this.signatureState.rightSigned = true;
      this.areQAChecksSigned = true;
      this.enableEnterNextCommand(this.areQaChecksValid, true);
    }
  }

  public getSignatureClasses(position: string): Record<string, boolean> {
    return {
      [position]: true
    };
  }

  public shouldShowSignatureContainer(): boolean {
    return this.isSignatureFeatureEnabled && this.leftSignatureQaChecks.length > 0 && this.rightSignatureQaChecks.length > 0;
  }

  private setupFilterProductionQaCheck(): void {
    let productionQaCheck: ProductionQaCheck[] = [];

    this.fields$.pipe(take(1)).subscribe((data: ProductionQaCheck[]) => {
      productionQaCheck = data.sort((a, b) => a.id - b.id); // ascending order

      if (productionQaCheck.length) {
        const qaCheckIds = [...new Set(productionQaCheck.map((item) => item.qaCheckId))];
        console.info('qaCheckIds', qaCheckIds);

        qaCheckIds.forEach((qaCheckId) => {
          // Ensure qaCheckId is valid
          if (qaCheckId == null) {
              console.warn(`Invalid qaCheckId encountered: ${qaCheckId}`);
              return;
          }

          // Filter productionQaCheck by qaCheckId
          const matchQaChecks = productionQaCheck?.filter((item) => item.qaCheckId === qaCheckId) || [];

          // Proceed only if there are matching QA checks
          if (matchQaChecks.length > 0) {
              const midIndex = Math.ceil(matchQaChecks.length / 2);

              // Safely slice the left and right parts
              const left = matchQaChecks.slice(0, midIndex);
              const right = matchQaChecks.slice(midIndex);

              // Push to respective arrays if valid
              if (left?.length > 0) {
                  this.leftSignatureQaChecks.push(...left);
              }

              if (right?.length > 0) {
                  this.rightSignatureQaChecks.push(...right);
              }
          } else {
              console.warn(`No matching QA checks found for qaCheckId: ${qaCheckId}`);
          }
        });
      }
    });
  }

  // removes stored signatures for all QA checks
  private removeSignature(): void {
    this.nextCommandService.enabled = this.isNextCommandEnabled = false;
    this.signatureState = { leftSigned: false, rightSigned: false };
    this.areQAChecksSigned = false;

    this.lineClearanceService.getQACheckFields(this.productionOrderId)
    .pipe(take(1))
    .subscribe((data: ProductionQaCheck[]) => {
      data.forEach(item => {
        if (item?.employeeSignature?.employeeSignatureId) {
          this.employeeSignatureDsService.resetSignature(
            item.employeeSignature.employeeSignatureId,
            true,
            this.workCenterService.getWorkCenterId()
          ).subscribe();
        }
      });
    });
  }

  private getSignatureComponent(position: string): SignatureComponent | null {
    // Logic to retrieve the correct SignatureComponent instance based on the position
    return position === 'left-signature' ? this.leftSignatureComponent : this.rightSignatureComponent;
  }

  private isFocusAvailable(): boolean {
    const modal = document.querySelector('p-dynamicdialog');

    if (modal) {
      return false;
    }

    return document.activeElement !== this.scanInput.nativeElement &&
      document.activeElement?.tagName !== 'INPUT' &&
      document.activeElement?.tagName !== 'SELECT';
  }

  private manageCapsLock(event?: KeyboardEvent | MouseEvent) {
    this.isCapsLockOn = event?.getModifierState('CapsLock') ? true : false;
  }

  private setFocusScanInput(): void {
    this.scanInput.nativeElement.focus();
    console.info('Line Clearance scan field received as focus');
  }

  private setCommands(): void {
    this.nextCommandService = new NextSetupSubphaseCommandService(this.setupFooterService);
    this.setupPhaseService.setSetupCommands(null, this.nextCommandService);
  }

  private updateNextCommand(isQaAvailable: boolean): void {
    if (isQaAvailable) {
      this.nextCommandService.setNavigationParams(RunSubPhaseType.SetupQaHistory, nav.routes.setupQaHistory);
    } else {
      this.nextCommandService.setNavigationParams(RunSubPhaseType.SetupParameter, nav.routes.setupParameters);
    }
  }

  private findParentElement(tagName: string): HTMLElement | null {
    let element = this.scanInput.nativeElement.parentElement;

    while (element && element.tagName.toLowerCase() !== tagName.toLowerCase()) {
      element = element.parentElement;
    }

    return element;
  }
}
